import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ExternalLinkIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input, inputClassName } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import DocumentInputMasked from '@/components/DocumentInputMasked';
import { useAuth } from '@/context/AuthContext';
import { useFetch } from '@/hooks/useFecth';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';
import { formatBirthday, formatDate } from '../utils/subscriberDates';
import ClubAddressField from './ClubAddressField';
import ClubPhoneInput from './ClubPhoneInput';
import SubscriptionDetailsModal from './SubscriptionDetailsModal';

const GENDER_ITEMS = {
    '': 'Selecione',
    male: 'Masculino',
    female: 'Feminino',
};


const subscriberSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
    email: z.string().min(1, 'E-mail é obrigatório').email('E-mail deve ter um formato válido'),
    birthday: z.string().min(1, 'Data de aniversário é obrigatória'),
    document: z
        .string()
            .min(11, 'Documento deve ter pelo menos 11 dígitos')
            .max(14, 'Documento deve ter no máximo 14 dígitos'),
    gender: z.enum(['male', 'female'], { message: 'Selecione o gênero' }),
    phone: z
        .string()
        .refine(value => String(value || '').replace(/\D/g, '').length >= 10, 'Informe o telefone com DDD'),
    status: z.boolean().optional(),
    zipcode: z.string().refine(value => String(value || '').replace(/\D/g, '').length === 8, 'Informe o CEP'),
    street: z.string().trim().min(1, 'Informe o logradouro'),
    number: z.string().trim().min(1, 'Informe o número'),
    complement: z.string().optional(),
    neighborhood: z.string().trim().min(1, 'Informe o bairro'),
    city: z.string().trim().min(1, 'Informe a cidade'),
    state: z.string().trim().length(2, 'Informe a UF (2 letras)'),
    address: z.string().optional(),
    addressId: z.union([z.number(), z.string()]).optional(),
    clubId: z.union([z.number(), z.string()]).optional(),
});

const defaultSubscriberValues = {
            name: '',
            email: '',
            document: '',
            gender: '',
            phone: '',
            birthday: '',
    status: true,
            zipcode: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            address: '',
    addressId: '',
};

export { formatBirthday, formatDate };

function toFormGender(value) {
    const normalized = String(value || '').trim().toLowerCase();
    if (['male', 'm', 'masculino'].includes(normalized)) return 'male';
    if (['female', 'f', 'feminino'].includes(normalized)) return 'female';
    return '';
}

function mapSubscriberToForm(data) {
    if (!data) return defaultSubscriberValues;

    return {
        id: data.id,
        name: data.name || '',
        email: data.email || '',
        document: data.document || '',
        gender: toFormGender(data.gender),
        phone: data.phone || '',
        birthday: formatBirthday(data.birthday),
        clubId: data.clubId,
        status: typeof data.status === 'boolean' ? data.status : true,
        zipcode: String(data.zipcode ?? '').replace(/\D/g, ''),
        street: data.street ?? '',
        number: data.number ?? '',
        complement: data.complement ?? '',
        neighborhood: data.neighborhood ?? '',
        city: data.city ?? '',
        state: data.state ?? '',
        address: data.address ?? '',
        addressId: data.addressId ?? '',
    };
}

function buildAddressSummary(values) {
    const parts = [values.street, values.neighborhood].filter(Boolean);
    const cityState = [values.city, values.state].filter(Boolean).join('-');
    if (cityState) parts.push(cityState);
    return parts.join(', ');
}

function SubscriberDataFields({ form, showStatus }) {
    const { control, register, formState, setValue } = form;
    const { errors } = formState;

    return (
        <FieldGroup>
            <Alert>
                <AlertTitle>Senha de acesso do assinante</AlertTitle>
                <AlertDescription>
                    A senha provisória são os <strong>6 primeiros dígitos do CPF/CNPJ</strong>. Um e-mail de boas-vindas
                    com esses dados é enviado automaticamente ao assinante após o cadastro.
                </AlertDescription>
                            </Alert>

            <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="subscriber-name">Nome</FieldLabel>
                <Input id="subscriber-name" placeholder="Nome" {...register('name')} />
                {errors.name ? <FieldError errors={[errors.name]} /> : null}
            </Field>

            <Field data-invalid={Boolean(errors.email)}>
                <FieldLabel htmlFor="subscriber-email">E-mail</FieldLabel>
                <Input id="subscriber-email" type="email" placeholder="E-mail" {...register('email')} />
                {errors.email ? <FieldError errors={[errors.email]} /> : null}
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
                <Field data-invalid={Boolean(errors.document)}>
                    <FieldLabel htmlFor="subscriber-document">Documento</FieldLabel>
                    <Controller
                                    name="document"
                        control={control}
                        render={({ field }) => (
                            <DocumentInputMasked
                                id="subscriber-document"
                                name={field.name}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                className={inputClassName}
                            />
                        )}
                    />
                    {errors.document ? <FieldError errors={[errors.document]} /> : null}
                </Field>

                <Field data-invalid={Boolean(errors.gender)}>
                    <FieldLabel htmlFor="subscriber-gender">Gênero</FieldLabel>
                    <Controller
                                    name="gender"
                        control={control}
                        render={({ field }) => (
                            <Select items={GENDER_ITEMS} value={field.value || ''} onValueChange={field.onChange}>
                                <SelectTrigger id="subscriber-gender" className="w-full">
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="">Selecione</SelectItem>
                                    <SelectItem value="male">Masculino</SelectItem>
                                    <SelectItem value="female">Feminino</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    />
                    {errors.gender ? <FieldError errors={[errors.gender]} /> : null}
                </Field>
            </div>

            <Field data-invalid={Boolean(errors.phone)}>
                <FieldLabel htmlFor="subscriber-phone">Telefone</FieldLabel>
                <Controller
                                name="phone"
                    control={control}
                    render={({ field }) => (
                        <ClubPhoneInput
                            id="subscriber-phone"
                            name={field.name}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        />
                    )}
                />
                {errors.phone ? <FieldError errors={[errors.phone]} /> : null}
            </Field>

            <Field data-invalid={Boolean(errors.birthday)}>
                <FieldLabel htmlFor="subscriber-birthday">Data de aniversário</FieldLabel>
                <Input id="subscriber-birthday" type="date" {...register('birthday')} />
                {errors.birthday ? <FieldError errors={[errors.birthday]} /> : null}
            </Field>

            <Field data-invalid={Boolean(errors.zipcode)}>
                <FieldLabel htmlFor="subscriber-zipcode">CEP</FieldLabel>
                <Controller
                                name="zipcode"
                    control={control}
                    render={({ field }) => (
                        <ClubAddressField
                            name={field.name}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            setValue={setValue}
                        />
                    )}
                />
                {errors.zipcode ? <FieldError errors={[errors.zipcode]} /> : null}
            </Field>

            <div className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                <div>
                    <p className="text-sm font-medium">Endereço</p>
                    <p className="text-xs text-muted-foreground">
                        Preenchido pelo CEP. Se o CEP não for encontrado, digite os campos.
                    </p>
                </div>

                <Field data-invalid={Boolean(errors.street)}>
                    <FieldLabel htmlFor="subscriber-street">Logradouro</FieldLabel>
                    <Input id="subscriber-street" placeholder="Rua, avenida..." {...register('street')} />
                    {errors.street ? <FieldError errors={[errors.street]} /> : null}
                </Field>

                <div className="grid gap-4 sm:grid-cols-2">
                    <Field data-invalid={Boolean(errors.neighborhood)}>
                        <FieldLabel htmlFor="subscriber-neighborhood">Bairro</FieldLabel>
                        <Input id="subscriber-neighborhood" {...register('neighborhood')} />
                        {errors.neighborhood ? <FieldError errors={[errors.neighborhood]} /> : null}
                    </Field>
                    <Field data-invalid={Boolean(errors.city)}>
                        <FieldLabel htmlFor="subscriber-city">Cidade</FieldLabel>
                        <Input id="subscriber-city" {...register('city')} />
                        {errors.city ? <FieldError errors={[errors.city]} /> : null}
                    </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-[120px_1fr_120px]">
                    <Field data-invalid={Boolean(errors.state)}>
                        <FieldLabel htmlFor="subscriber-state">UF</FieldLabel>
                        <Input
                            id="subscriber-state"
                            maxLength={2}
                            placeholder="UF"
                            {...register('state', {
                                setValueAs: value => String(value || '').toUpperCase(),
                            })}
                        />
                        {errors.state ? <FieldError errors={[errors.state]} /> : null}
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="subscriber-complement">Complemento</FieldLabel>
                        <Input id="subscriber-complement" {...register('complement')} />
                    </Field>
                    <Field data-invalid={Boolean(errors.number)}>
                        <FieldLabel htmlFor="subscriber-number">Número</FieldLabel>
                        <Input id="subscriber-number" {...register('number')} />
                        {errors.number ? <FieldError errors={[errors.number]} /> : null}
                    </Field>
                </div>
            </div>

            {showStatus ? (
                <Card className="border-destructive/40 bg-destructive/5 py-0 ring-destructive/20">
                    <CardContent className="flex items-center justify-between gap-4 px-3 py-2.5">
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-destructive">Usuário banido</p>
                            <p className="text-xs text-destructive/80">
                                Defina se o assinante está banido.
                            </p>
                        </div>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => {
                                const banned = field.value === false;
                                return (
                                    <div className="flex shrink-0 items-center gap-3">
                                        <span
                                            className={cn(
                                                'text-sm font-semibold',
                                                banned ? 'text-destructive' : 'text-muted-foreground',
                                            )}
                                        >
                                            {banned ? 'Banido' : 'Liberado'}
                                        </span>
                                        <Switch
                                            id="subscriber-status"
                                            checked={banned}
                                            aria-label={banned ? 'Banido' : 'Liberado'}
                                            className={cn(
                                                banned &&
                                                    'data-checked:bg-destructive data-checked:border-destructive',
                                            )}
                                            onCheckedChange={isBanned => field.onChange(!isBanned)}
                                        />
                                    </div>
                                );
                            }}
                        />
                    </CardContent>
                </Card>
            ) : null}
        </FieldGroup>
    );
}


function SubscriberForm({ data, mutate, isEditMode = false, isLoadingData = false, mutatePerson }) {
    const { open, handleClose, handleOpen } = useDialog('#assinantes');
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const { user } = useAuth();
    const { club } = useClub();
    const [tab, setTab] = useState('dados');
    const [detailsOpen, setDetailsOpen] = useState(false);

    const form = useForm({
        resolver: zodResolver(subscriberSchema),
        defaultValues: data ? mapSubscriberToForm(data) : defaultSubscriberValues,
    });

    const subscriberId = form.watch('id');
    const isEditing = isEditMode || Boolean(subscriberId);

    useEffect(() => {
        if (data) {
            form.reset(mapSubscriberToForm(data));
        }
    }, [data, form]);

    const refreshAll = async () => {
        await mutatePerson?.();
        await mutate?.();
    };

    const onSubmit = async values => {
        try {
            const payload = {
                name: values.name,
                email: values.email,
                document: values.document,
                phone: values.phone,
                birthday: values.birthday,
                gender: values.gender,
                status: values.status,
                addressId: values.addressId || undefined,
                zipcode: values.zipcode,
                street: values.street,
                number: values.number,
                complement: values.complement,
                neighborhood: values.neighborhood,
                city: values.city,
                state: values.state,
                address: buildAddressSummary(values) || values.address || '',
            };

            if (!values.id) {
                const response = await server.post('club/subscribers', payload);
                const created = response?.data?.data;
                const personId = created?.id;
                if (!personId) {
                    throw { message: 'Assinante criado, mas o id não veio na resposta.' };
                }
                handleOpen(personId);
                enqueueSnackbar(`Assinante ${values.name} adicionado.`);
            } else {
                await server.put(`club/subscribers/${values.id}`, payload);
                enqueueSnackbar(`Assinante ${values.name} alterado.`);
            }

            await refreshAll();
            handleClose();
        } catch (error) {
            setErrors(error);
        }
    };

    const handleDelete = async () => {
        if (!user?.super) return;
        try {
            await confirm({
                title: 'Exclusão permanente (super)',
                description:
                    'Hard delete: remove o assinante, dependentes vinculados, assinatura, benefícios e dados relacionados. Não pode ser desfeito.',
                confirmationText: 'Excluir permanentemente',
                confirmationButtonProps: { variant: 'destructive' },
            });

            await server.delete(`club/subscribers/${subscriberId}`, {
                params: { hard: true },
            });
            enqueueSnackbar('Assinante excluído permanentemente.');
            await mutate?.();
            handleClose();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const { isSubmitting } = form.formState;

    return (
        <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
            <ScrollDialogContent className="sm:max-w-xl">
                <ScrollDialogHeader>
                    <DialogTitle>Adicionando/editando assinante</DialogTitle>
                </ScrollDialogHeader>

                <DialogBody>
                    {isLoadingData ? (
                        <div className="flex items-center justify-center gap-2 py-16">
                            <Spinner className="size-5" />
                            <span className="text-sm text-muted-foreground">Carregando assinante…</span>
                        </div>
                    ) : isEditing ? (
                        <Tabs value={tab} onValueChange={setTab}>
                            <TabsList className="mb-4 grid w-full grid-cols-2">
                                <TabsTrigger value="dados">Dados</TabsTrigger>
                                <TabsTrigger value="assinatura">Assinatura</TabsTrigger>
                            </TabsList>

                            <TabsContent value="dados">
                                <form id="subscriber-data-form" onSubmit={form.handleSubmit(onSubmit)}>
                                    <SubscriberDataFields form={form} showStatus />
                                </form>
                            </TabsContent>

                            <TabsContent value="assinatura">
                                <div className="flex flex-col gap-4">
                                    <Alert>
                                        <AlertTitle>A assinatura mudou de lugar</AlertTitle>
                                        <AlertDescription>
                                            Para deixar tudo mais organizado, plano, ciclo, status, cancelamento
                                            {user?.super ? ' e vínculo Asaas' : ''} agora ficam em{' '}
                                            <strong>Detalhes da Assinatura</strong>, junto com pagamentos e
                                            dependentes. Aqui ficam só os dados cadastrais.
                                        </AlertDescription>
                                    </Alert>
                                    <Button type="button" className="w-fit" onClick={() => setDetailsOpen(true)}>
                                        <ExternalLinkIcon className="size-4" />
                                        Abrir Detalhes da Assinatura
                                    </Button>
                                </div>
                            </TabsContent>
                        </Tabs>
                    ) : (
                        <form id="subscriber-data-form" onSubmit={form.handleSubmit(onSubmit)}>
                            <SubscriberDataFields form={form} showStatus={false} />
                        </form>
                    )}
                </DialogBody>

                {!isLoadingData && (!isEditing || tab === 'dados') ? (
                    <>
                        <Separator />
                        <ScrollDialogFooter>
                            {isEditing && user?.super ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="mr-auto text-destructive hover:text-destructive"
                                    disabled={user?.id === subscriberId}
                                    onClick={handleDelete}
                                >
                                    Excluir permanentemente
                                </Button>
                            ) : null}
                            <Button type="submit" form="subscriber-data-form" disabled={isSubmitting}>
                                {isSubmitting ? <Spinner className="size-4" /> : 'Salvar e continuar'}
                            </Button>
                        </ScrollDialogFooter>
                    </>
                ) : null}
            </ScrollDialogContent>

            {isEditing && data ? (
                <SubscriptionDetailsModal
                    club={club}
                    open={detailsOpen}
                    onClose={() => setDetailsOpen(false)}
                    subscriber={data}
                    onChanged={refreshAll}
                />
            ) : null}
        </Dialog>
    );
}

function Edit({ id, mutate }) {
    const { data, isFetching, mutate: mutatePerson } = useFetch(`club/subscribers/${id}`);

    return (
        <SubscriberForm
            key={id}
            data={data}
            mutate={mutate}
            mutatePerson={mutatePerson}
            isEditMode
            isLoadingData={isFetching && !data}
        />
    );
}

export default function DialogSubscriber({ mutate }) {
    const { open, id } = useDialog('#assinantes');

    if (!open) return null;
    if (id) return <Edit id={id} mutate={mutate} />;
    return <SubscriberForm mutate={mutate} />;
}
